import { useEffect } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Avatar, PopoverDialog, rgba, styles, Text, Truncated, usePopoverDialog } from '@frontend/design-system';
import { getRelativeTime } from '../../helpers';
import { useLocationTimezone } from '../../hooks/use-location-timezone';
import { PersonNotesComponent } from '../PersonSelector/PersonNotesComponent';
import { TextWithTooltip } from '../text-with-tooltip';
import { AnimWrapper } from './anim-wrapper';
import { AvatarCheckbox } from './avatar-checkbox';
import { CalendarIcon, OptOutIcon } from './icons';

interface Props {
  personName: string;
  notes?: string;
  futureApptDate?: string; // date string
  createdAt: string; // date string
  optedOut?: boolean;
  selected?: boolean;
  editing?: boolean;
  selectionModeOn?: boolean;
  highlight?: boolean;
  isSavingNote?: boolean;
  onAvatarClick?: () => void;
  onSelectToggle?: () => void;
  onHighlightComplete?: () => void;
  onSaveNote?: (newNote: string) => Promise<void>;
  onEditComplete?: () => void;
  formatDateTime: ReturnType<ReturnType<typeof useLocationTimezone>['formatDateAndTimeByLocation']>;
}

export const WaitlistItem = ({
  personName,
  notes = '',
  futureApptDate,
  createdAt,
  optedOut = false,
  onAvatarClick,
  selected = false,
  editing = false,
  onSelectToggle,
  selectionModeOn,
  highlight = false,
  onHighlightComplete,
  onEditComplete,
  isSavingNote = false,
  onSaveNote,
  formatDateTime,
}: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const addEditNotePopover = usePopoverDialog<HTMLDivElement>({
    placement: 'bottom-end',
    onClose: onEditComplete,
    initialOffset: { x: 0, y: 40 },
  });

  useEffect(() => {
    if (editing) addEditNotePopover.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const handleSaveNoteClick = (newNote: string) => onSaveNote?.(newNote).then(addEditNotePopover.close);

  const relativeTime = getRelativeTime(createdAt);
  const relativeTimeTooltip = formatDateTime(createdAt, 'MM/DD/YY h:mm A', true);
  const futureApptTooltip = futureApptDate
    ? t(`Appointment on {{date}}`, { date: formatDateTime(futureApptDate, 'MM/DD/YY', true) })
    : '';

  return (
    <AnimWrapper highlight={highlight} onAnimationComplete={onHighlightComplete}>
      <section
        css={[
          containerWithHoverStyle,
          selected && selectedContainerStyle,
          { cursor: optedOut ? 'not-allowed' : 'pointer' },
        ]}
        onClick={optedOut ? undefined : onSelectToggle}
        ref={addEditNotePopover.refs.setPositionReference}
      >
        <div css={styles.flexCenter}>
          {selectionModeOn ? (
            <AvatarCheckbox isSelected={selected} disabled={optedOut} onChange={onSelectToggle} />
          ) : (
            <Avatar
              size='small'
              name={personName}
              onClick={(e) => {
                e?.stopPropagation();
                onAvatarClick?.();
              }}
            />
          )}
        </div>
        <div css={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div css={contentRowStyle} data-content-row>
            <Truncated as={Text} size='medium' weight='semibold' css={{ width: 'auto', maxWidth: 200 }}>
              {personName}
            </Truncated>
            <div css={{ display: 'flex', gap: theme.spacing(0.5) }}>
              {!!futureApptDate && <CalendarIcon tooltipText={futureApptTooltip} />}
              {optedOut && <OptOutIcon />}
            </div>
            <TextWithTooltip
              size='small'
              color='subdued'
              css={{ display: 'flex', marginLeft: 'auto', minWidth: 'max-content' }}
              tooltipText={relativeTimeTooltip}
              tooltipPlacement='top-end'
            >
              {relativeTime}
            </TextWithTooltip>
          </div>
          <Text size='small' color={notes ? 'default' : 'subdued'} css={notesStyle} title={notes || ''}>
            {notes || t('No notes')}
          </Text>
        </div>
      </section>
      <PopoverDialog
        {...addEditNotePopover.getDialogProps()}
        css={{ width: 388, padding: 0, borderRadius: theme.borderRadius.small }}
      >
        <PersonNotesComponent
          isLoading={isSavingNote}
          heading={notes ? t('Edit Note') : t('Add Note')}
          onPrimaryClick={handleSaveNoteClick}
          onSecondaryClick={addEditNotePopover.close}
          primaryButtonText={t('Save')}
          note={notes || ''}
        />
      </PopoverDialog>
    </AnimWrapper>
  );
};

const containerWithHoverStyle = css({
  maxHeight: 82,
  width: '100%',
  padding: theme.spacing(1.5, 3),
  display: 'flex',
  gap: theme.spacing(2),
  borderBottom: `1px solid ${theme.colors.neutral10}`,
  transition: 'background 0.2s ease-in-out',
  ':hover': {
    background: theme.colors.neutral5,
    '& [data-content-row]': {
      maxWidth: 'calc(100% - 80px)',
    },
  },
});

const selectedContainerStyle = css({
  background: rgba(theme.colors.primary50, 0.1),
  ':hover': {
    background: rgba(theme.colors.primary50, 0.15),
  },
});

const notesStyle = css({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const contentRowStyle = css({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  maxWidth: '100%',
  transition: 'max-width 0.2s ease-in-out',
});
